import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, ButtonBase, Collapse } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { MainMenuItem, SubMenuItem, ChildMenuItem } from '..';
import { DrawerContentProps } from './DrawerContent.props';
import { expandableIds, findOpenPath } from './expandPath';
import { getIconForSection } from './iconMap';
import {
  findActiveUrl,
  isActive as isActiveUrl,
  sectionContainsActive,
  subItemContainsActive,
} from '../navActive';
import {
  activeItemSx,
  borderLeftBaseSx,
  chevronSx,
  groupHeaderSx,
  iconSx,
  navItemBaseSx,
  nestedNavItemSx,
  sectionHeaderButtonSx,
  sectionHeaderSx,
} from './navStyles';

/**
 * Returns true when a click event includes a modifier key or is not a primary
 * left-click. In those cases we should let the browser handle the anchor
 * natively (e.g. open in a new tab) instead of intercepting it.
 */
const isModifiedClick = (e: React.MouseEvent) =>
  e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey;

/**
 * See `findActiveUrl` — a detail route not itself in the menu (opened from a
 * search result or another page's link, rather than the nav) still resolves
 * to its owning list page.
 */
const useActiveUrl = (menuData: MainMenuItem[], pathname: string) =>
  useMemo(() => findActiveUrl(menuData, pathname), [menuData, pathname]);

export const DrawerContent: React.FC<DrawerContentProps> = ({ menuData, handleMenuItemClick }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [openSectionId, setOpenSectionId] = useState<string | null>(
    () => findOpenPath(menuData, location.pathname).sectionId
  );
  const [openGroupId, setOpenGroupId] = useState<string | null>(
    () => findOpenPath(menuData, location.pathname).groupId
  );

  // Resync to whichever section/group contains the active route — on every
  // navigation (direct URL, refresh, back/forward, in-app link, all surface
  // here as a `location.pathname` change) and also when the menu's shape
  // changes (MainLayout renders a fallback menu before permissions resolve,
  // then swaps in the real one at the same pathname). Always overwrites both
  // values outright — there's deliberately no "remember what was manually
  // open before" here: only one path is ever open, and navigating (or the
  // real menu arriving) always resets it to match where you are.
  const structureKey = expandableIds(menuData).join('|');
  useEffect(() => {
    const { sectionId, groupId } = findOpenPath(menuData, location.pathname);
    setOpenSectionId(sectionId);
    setOpenGroupId(groupId);
    // menuData itself isn't a dep — structureKey already captures every shape
    // change that matters, without refiring on a same-shape re-render where
    // the array only got a new reference identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, structureKey]);

  /** Opens this section (closing whatever else was open, per Option A —
   * reopening a section never remembers which group was last open in it) or
   * closes it if it's already the open one. */
  const toggleSection = (id: string) => {
    setOpenSectionId((prev) => (prev === id ? null : id));
    setOpenGroupId(null);
  };

  /** Only ever touches which group is open — a group can't be visible unless
   * its section already is, so there's nothing else to reset here. */
  const toggleGroup = (id: string) => {
    setOpenGroupId((prev) => (prev === id ? null : id));
  };

  const activeUrl = useActiveUrl(menuData, location.pathname);
  const isActive = (url: string) => isActiveUrl(activeUrl, url);

  /**
   * Click handler for nav links. Lets ctrl/cmd/shift/middle-clicks fall
   * through to the browser (so the link opens in a new tab/window) and only
   * intercepts plain left-clicks for the in-app navigation + same-path
   * "reload via /loader" behaviour.
   */
  const handleLinkClick = (url: string) => (e: React.MouseEvent) => {
    if (isModifiedClick(e)) return;
    handleMenuItemClick(true);
    if (window.location.pathname === url) {
      e.preventDefault();
      setTimeout(() => navigate('/loader'), 1);
    }
    // Otherwise let <Link> handle navigation via React Router.
  };

  /** `isNested` selects the deeper indent used by third-level links. */
  const renderNavItem = (name: string, url: string, isNested = false) => {
    const itemSx = isNested ? nestedNavItemSx : navItemBaseSx;
    if (!url) {
      return (
        <Box key={name} sx={itemSx}>
          {name}
        </Box>
      );
    }
    return (
      <Box
        key={url}
        component={Link}
        to={url}
        onClick={handleLinkClick(url)}
        aria-current={isActive(url) ? 'page' : undefined}
        sx={{ ...itemSx, ...(isActive(url) ? activeItemSx : {}) }}
      >
        {name}
      </Box>
    );
  };

  /** A collapsible group of third-level links (e.g. Jobs > Quick Views). */
  const renderGroup = (group: SubMenuItem, children: ChildMenuItem[]) => {
    const isExpanded = openGroupId === group.id;
    // Surface the active state on the header while collapsed, so a manually
    // closed group still shows where you are.
    const hasActiveChild = subItemContainsActive(activeUrl, group);
    return (
      <>
        <ButtonBase
          disableRipple
          onClick={() => toggleGroup(group.id)}
          aria-expanded={isExpanded}
          aria-current={!isExpanded && hasActiveChild ? 'true' : undefined}
          sx={{
            ...groupHeaderSx,
            ...(!isExpanded && hasActiveChild ? activeItemSx : {}),
          }}
        >
          {group.name}
          <ChevronRightIcon sx={chevronSx(isExpanded)} />
        </ButtonBase>
        <Collapse in={isExpanded} timeout={200}>
          {children.map((child) => renderNavItem(child.name, child.url, true))}
        </Collapse>
      </>
    );
  };

  const renderSubItems = (subs: SubMenuItem[]) =>
    subs.map((sub) => (
      <React.Fragment key={sub.id}>
        {sub.subMenuItems && sub.subMenuItems.length > 0
          ? renderGroup(sub, sub.subMenuItems)
          : renderNavItem(sub.name, sub.url)}
      </React.Fragment>
    ));

  const renderMenuItem = (item: MainMenuItem) => {
    const Icon = getIconForSection(item.name);

    // Top-level item with no children (e.g. Home)
    if (!item.subMenuItems || item.subMenuItems.length === 0) {
      const url = item.url ?? '';
      const itemSx = {
        ...sectionHeaderSx,
        ...borderLeftBaseSx,
        ...(isActive(url) ? activeItemSx : {}),
      };
      if (!url) {
        return (
          <Box key={item.id} sx={itemSx}>
            <Icon sx={iconSx} />
            {item.name}
          </Box>
        );
      }
      return (
        <Box
          key={item.id}
          component={Link}
          to={url}
          onClick={handleLinkClick(url)}
          aria-current={isActive(url) ? 'page' : undefined}
          sx={itemSx}
        >
          <Icon sx={iconSx} />
          {item.name}
        </Box>
      );
    }

    // Collapsible section
    const isExpanded = openSectionId === item.id;
    // Same "show where you are while collapsed" treatment as the nested
    // group level (renderGroup above) — a section can now end up collapsed
    // while still containing the active route (e.g. the user manually
    // collapsed it, or navigated to a different section), which couldn't
    // happen before sections could collapse at all.
    const hasActiveChild = sectionContainsActive(activeUrl, item);
    return (
      <Box key={item.id} sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)', py: 0.25 }}>
        <ButtonBase
          disableRipple
          onClick={() => toggleSection(item.id)}
          sx={{
            ...sectionHeaderButtonSx,
            ...(!isExpanded && hasActiveChild ? activeItemSx : {}),
          }}
          aria-expanded={isExpanded}
          aria-current={!isExpanded && hasActiveChild ? 'true' : undefined}
        >
          <Icon sx={iconSx} />
          {item.name}
          <ChevronRightIcon sx={chevronSx(isExpanded)} />
        </ButtonBase>
        <Collapse in={isExpanded} timeout={200}>
          {renderSubItems(item.subMenuItems)}
        </Collapse>
      </Box>
    );
  };

  return (
    <Box component="nav" aria-label="Main navigation" sx={{ overflowY: 'auto', flex: 1 }}>
      {menuData.map((item) => (
        <React.Fragment key={item.id}>{renderMenuItem(item)}</React.Fragment>
      ))}
    </Box>
  );
};
